import { COVERAGE_TILE_DEFAULTS, COVERAGE_TILE_TIERS, type CoverageTileTier } from './catalog/cart-catalog';
import { createBuilderId, type BuilderNode } from './useBuilderTree';

const COVERAGE_TYPE = 'ew-coverage';
const TILE_TYPE = 'ew-coverage-option';

/** Where an `ew-coverage` node kept each tile's wording before the tiles became nodes of their own. */
const LEGACY_ATTRS: Record<CoverageTileTier, { features: string; priceLabel?: string; title: string }> = {
  none: { features: 'none-features', priceLabel: 'none-price-label', title: 'none-title' },
  shipping: { features: 'basic-features', title: 'basic-title' },
  warranty: { features: 'warranty-features-template', title: 'warranty-title-template' },
};

function isTileTier(value: unknown): value is CoverageTileTier {
  return typeof value === 'string' && (COVERAGE_TILE_TIERS as readonly string[]).includes(value);
}

/** `badge-tier` named a chip (`warranty-2`); every warranty length now shares the one warranty tile. */
function legacyBadgeTier(badgeTier: unknown): CoverageTileTier | null {
  if (typeof badgeTier !== 'string') return null;
  if (badgeTier.startsWith('warranty-')) return 'warranty';

  return isTileTier(badgeTier) ? badgeTier : null;
}

function tileFromLegacy(node: BuilderNode, tier: CoverageTileTier): BuilderNode {
  const attrs = node.attributes ?? {};
  const legacy = LEGACY_ATTRS[tier];
  const pick = (attr: string | undefined, fallback: string): string => {
    const value = attr ? attrs[attr] : undefined;

    return typeof value === 'string' ? value : fallback;
  };
  const badgeText = legacyBadgeTier(attrs['badge-tier']) === tier ? String(attrs['badge-text'] ?? '') : '';

  return {
    __builderId: createBuilderId(),
    attributes: {
      'badge-text': badgeText,
      features: pick(legacy.features, COVERAGE_TILE_DEFAULTS[tier].features),
      // The runtime showed a formatted zero for No Coverage unless told otherwise; an upgrade keeps that.
      'price-label': pick(legacy.priceLabel, ''),
      tier,
      title: pick(legacy.title, COVERAGE_TILE_DEFAULTS[tier].title),
    },
    type: TILE_TYPE,
  };
}

function upgradeCoverageNode(node: BuilderNode): void {
  const existing = node.children ?? [];
  const tileFor = (tier: CoverageTileTier) =>
    existing.find(child => child.type === TILE_TYPE && child.attributes?.tier === tier);
  const others = existing.filter(child => child.type !== TILE_TYPE || !isTileTier(child.attributes?.tier));

  // Tiles draw in tier order whatever the JSON order, so the Layers list is kept in that order too.
  node.children = [...COVERAGE_TILE_TIERS.map(tier => tileFor(tier) ?? tileFromLegacy(node, tier)), ...others];
}

/**
 * Gives every `ew-coverage` node one `ew-coverage-option` child per tier, so each tile is a node
 * the builder can select and edit. A node saved before the tiles were nodes carried the wording as
 * its own attributes: those seed the tiles and are left in place. The new runtime ignores them
 * once a tile exists, while a bundle that predates the tiles still reads them, so the merchant's
 * wording survives a late or rolled-back bundle. Idempotent, and a no-op for every other node type.
 */
export function ensureCoverageTiles(nodes: BuilderNode[]): BuilderNode[] {
  nodes.forEach(node => {
    if (node.type === COVERAGE_TYPE) upgradeCoverageNode(node);
    else if (node.children) ensureCoverageTiles(node.children);
  });

  return nodes;
}
